import { isForecastEngineJobActive } from '@gozoki/api-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '~/lib/tuyau'
import type { PermissionTuple } from '~/utils/ability'

/** Lire les réglages du moteur (et en lancer une simulation, avec la lecture du pilotage). */
export const ENGINE_SETTINGS_READ: PermissionTuple = ['read', 'previsions.engine.settings']
/** Appliquer des réglages (« Appliquer » d'une simulation), ce qui relance la dernière passe. */
export const ENGINE_SETTINGS_EDIT: PermissionTuple = ['edit', 'previsions.engine.settings']

/** Rafraîchissement tant qu'une relance ou une passe tourne : le statut affiché suit le worker. */
const ACTIVE_POLL_MS = 4000

export const engineSettingsQueryOptions = () => api.forecastEngineSettings.show.queryOptions({})

export type EngineSettingsOverview = NonNullable<
  ReturnType<typeof useEngineSettingsOverview>['data']
>

/**
 * Réglages en vigueur, historique et statut de la dernière relance, suivis tant qu'elle tourne.
 * `enabled` : faux sans le droit de lire les réglages (tuile du Pilotage). `poll` : faux tant que
 * le statut n'est pas affiché (tuile du Pilotage, modale fermée), qui n'a pas à suivre la passe.
 */
export function useEngineSettingsOverview({
  enabled = true,
  poll = true,
}: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    ...engineSettingsQueryOptions(),
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!poll || data === undefined) return false
      return isForecastEngineJobActive(data.current?.rerun?.status) || data.passRunning
        ? ACTIVE_POLL_MS
        : false
    },
  })
}

/**
 * Applique des réglages. Refus (passe ou relance en cours) comme succès, la vue d'ensemble des
 * réglages est rechargée avant que l'appel se termine : le statut affiché doit suivre la base.
 */
export function useApplyEngineSettings() {
  const queryClient = useQueryClient()
  return useMutation(
    api.forecastEngineSettings.apply.mutationOptions({
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: engineSettingsQueryOptions().queryKey }),
    }),
  )
}

/** Relance la relance en échec de la version en vigueur, avec le même rechargement qu'Appliquer. */
export function useRelaunchEngineRerun() {
  const queryClient = useQueryClient()
  return useMutation(
    api.forecastEngineSettings.relaunchRerun.mutationOptions({
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: engineSettingsQueryOptions().queryKey }),
    }),
  )
}
